import { cn } from '@repo/ui/lib/utils';

/**
 * Configurable product-card badges, driven entirely by Shopify product tags so
 * they ride along with the existing `ProductCard` data (no extra fetch / prop
 * threading). Two surfaces:
 *   - image badges  → top-left corner of the card image (stacked, multiple)
 *   - title badge   → a single promo chip rendered under the product title
 *
 * Tag scheme (case-insensitive prefix, optional tone segment):
 *   badge:LABEL            → top-left image badge (neutral by default)
 *   badge:TONE:LABEL       → top-left image badge with an explicit tone
 *   promo:LABEL            → under-title badge (primary by default)
 *   promo:TONE:LABEL       → under-title badge with an explicit tone
 *
 * Examples: `badge:NEW` (auto secondary/gold), `badge:primary:LIMITED`,
 * `promo:BUY 2 GET 2 FREE` (auto primary), `promo:secondary:Gift With Purchase`.
 *
 * Tones map to the app's brand palette (globals.css @theme), never ad-hoc
 * colors, so badges stay on-brand and pick up per-tenant theme overrides.
 *
 * Status badges (Sold Out / Sale) are derived from product data, not tags. A
 * sold-out card shows only "Sold Out"; a markdown shows only the Sale badge —
 * it replaces any tagged badges in the image corner, which render at full
 * price only.
 */

export type BadgeTone = 'neutral' | 'primary' | 'secondary' | 'accent' | 'america';

export interface ProductBadge {
	label: string;
	tone: BadgeTone;
}

export interface ResolvedProductBadges {
	/** Top-left, stacked over the image. */
	imageBadges: ProductBadge[];
	/** Single chip under the product title. */
	titleBadge: ProductBadge | null;
}

const BADGE_PREFIX = 'badge:';
const PROMO_PREFIX = 'promo:';

// Brand palette only (see --color-brand-* in globals.css). `neutral` is the
// plain outlined chip; the rest map straight to brand tokens.
const TONE_CLASS: Record<BadgeTone, string> = {
	neutral: 'border border-neutral-300 bg-white text-neutral-900',
	primary: 'bg-brand-primary text-white',
	secondary: 'bg-brand-secondary text-neutral-900',
	accent: 'bg-brand-accent text-white',
	// Seasonal patriotic tone (America 250): red→blue gradient, white text.
	// Off-palette by design — see --color-america-* in globals.css. Stops are
	// referenced as explicit vars so the gradient renders regardless of whether
	// Tailwind emits `from-*`/`to-*` utilities for these custom theme tokens.
	america:
		'bg-gradient-to-r from-[var(--color-america-red)] to-[var(--color-america-blue)] text-white',
};

// Merchant-friendly aliases → canonical tone. Keeps the tag scheme forgiving
// (e.g. `badge:gold:NEW`, `promo:red:...`) while the palette stays fixed.
const TONE_ALIASES: Record<string, BadgeTone> = {
	neutral: 'neutral',
	white: 'neutral',
	gray: 'neutral',
	grey: 'neutral',
	primary: 'primary',
	dark: 'primary',
	black: 'primary',
	secondary: 'secondary',
	gold: 'secondary',
	tan: 'secondary',
	cream: 'secondary',
	accent: 'accent',
	red: 'accent',
	sale: 'accent',
	america: 'america',
	usa: 'america',
	patriot: 'america',
	patriotic: 'america',
	july4: 'america',
	independence: 'america',
};

// When no explicit tone is given, common keywords still pick a meaningful tone
// (NEW gets the brand-secondary highlight, anything "sale" gets the accent).
const KEYWORD_TONE: ReadonlyArray<readonly [string, BadgeTone]> = [
	['new', 'secondary'],
	['sale', 'accent'],
	['america', 'america'],
	['usa', 'america'],
];

function parseTaggedBadge(value: string, defaultTone: BadgeTone): ProductBadge | null {
	const trimmed = value.trim();
	if (!trimmed) {
		return null;
	}

	// Optional leading `TONE:` segment. Guard with hasOwn so inherited Object
	// keys (`constructor`, `toString`, …) never read as a tone.
	const separatorIndex = trimmed.indexOf(':');
	if (separatorIndex !== -1) {
		const maybeTone = trimmed.slice(0, separatorIndex).trim().toLowerCase();
		if (Object.hasOwn(TONE_ALIASES, maybeTone)) {
			const label = trimmed.slice(separatorIndex + 1).trim();
			// Valid tone but empty label (e.g. `badge:primary:`) — drop the tag
			// rather than leaking the raw tone token as visible text.
			return label ? { label, tone: TONE_ALIASES[maybeTone] } : null;
		}
	}

	// No explicit tone — fall back to a whole-word keyword match (so "Renewal"
	// doesn't match "new"), then the prefix default.
	const words = trimmed
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean);
	const keyword = KEYWORD_TONE.find(([word]) => words.includes(word));
	return { label: trimmed, tone: keyword ? keyword[1] : defaultTone };
}

/**
 * Rounded discount percentage for the auto Sale badge, from Money amount
 * strings. Null when there is no real markdown (or amounts don't parse).
 */
export function salePercentOff(
	priceAmount: string | null | undefined,
	compareAtAmount: string | null | undefined,
): number | null {
	const price = Number.parseFloat(priceAmount ?? '');
	const compareAt = Number.parseFloat(compareAtAmount ?? '');
	if (!Number.isFinite(price) || !Number.isFinite(compareAt)) return null;
	if (compareAt <= price || price <= 0) return null;
	return Math.round(((compareAt - price) / compareAt) * 100);
}

function parseTaggedImageBadges(tags: string[], defaultTone: BadgeTone): ProductBadge[] {
	const badges: ProductBadge[] = [];
	for (const tag of tags) {
		if (tag.toLowerCase().startsWith(BADGE_PREFIX)) {
			const badge = parseTaggedBadge(tag.slice(BADGE_PREFIX.length), defaultTone);
			if (badge) {
				badges.push(badge);
			}
		}
	}
	return badges;
}

export function resolveProductBadges({
	tags,
	availableForSale,
	isOnSale,
	percentOff,
}: {
	tags?: string[] | null;
	availableForSale: boolean;
	isOnSale: boolean;
	/** Rounded discount percentage; labels the auto sale badge "Sale · N%". */
	percentOff?: number | null;
}): ResolvedProductBadges {
	// Sold-out cards intentionally surface only the "Sold Out" status badge.
	if (!availableForSale) {
		return { imageBadges: [{ label: 'Sold Out', tone: 'neutral' }], titleBadge: null };
	}

	const allTags = tags ?? [];
	const imageBadges: ProductBadge[] = [];

	// A markdown owns the image corner: the Sale badge ("Sale · 35%" when the
	// discount is known) REPLACES tagged badges (e.g. LIMITED EDITION) so the
	// discount is never hidden. Tagged badges show at full price only.
	if (isOnSale) {
		const label = percentOff && percentOff > 0 ? `Sale · ${percentOff}%` : 'Sale';
		imageBadges.push({ label, tone: 'accent' });
	} else {
		imageBadges.push(...parseTaggedImageBadges(allTags, 'neutral'));
	}

	const promoTag = allTags.find((tag) => tag.toLowerCase().startsWith(PROMO_PREFIX));
	const titleBadge = promoTag
		? parseTaggedBadge(promoTag.slice(PROMO_PREFIX.length), 'primary')
		: null;

	// Drop duplicates (e.g. the same `badge:` tag applied twice).
	const seen = new Set<string>();
	const dedupedImageBadges = imageBadges.filter((badge) => {
		const key = `${badge.tone}|${badge.label.toLowerCase()}`;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});

	return { imageBadges: dedupedImageBadges, titleBadge };
}

const BADGE_CHIP_BASE =
	'inline-flex w-fit items-center rounded px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide';

export function ProductBadgeChip({
	badge,
	className,
}: {
	badge: ProductBadge;
	className?: string;
}) {
	return (
		<span className={cn(BADGE_CHIP_BASE, TONE_CLASS[badge.tone], className)}>{badge.label}</span>
	);
}

export function ProductBadgeStack({
	badges,
	className,
	chipClassName,
}: {
	badges: ProductBadge[];
	className?: string;
	chipClassName?: string;
}) {
	if (badges.length === 0) {
		return null;
	}

	return (
		<div className={className}>
			{badges.map((badge) => (
				// Unique by construction: resolveProductBadges dedupes by tone+label.
				<ProductBadgeChip
					key={`${badge.tone}-${badge.label}`}
					badge={badge}
					className={chipClassName}
				/>
			))}
		</div>
	);
}
